import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError, GymMembershipPlan } from '../api';
import Icon from '../design/Icon';
import { inr } from '../design/atoms';
import { featureChips, durationLabel } from '../planLabels';
import MemberStorePage from './MemberStorePage';
import type { MemberGym } from '../hooks/useMemberGyms';

/* Membership-plan browsing (work order B2): name, price, duration,
   description, and the features blob as readable chips. Browse-only — no
   purchase flow; the front desk closes the sale. */

interface OutletCtx {
  activeGym: MemberGym | null;
  activeGymId: string | null;
}

export default function MemberPlansPage() {
  const { token } = useMemberAuth();
  const { activeGym, activeGymId } = useOutletContext<OutletCtx>();
  const navigate = useNavigate();

  const [plans, setPlans] = useState<GymMembershipPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!token || !activeGymId) return;
    setLoading(true);
    setError(null);
    mfetch(token, `/member/gyms/${activeGymId}/plans`)
      .then(r => setPlans(r.data ?? []))
      .catch(e => setError(e instanceof MemberApiError ? e.message : 'Could not load plans'))
      .finally(() => setLoading(false));
  }, [token, activeGymId]);

  useEffect(load, [load]);

  if (!activeGymId) return null;
  // Gyms selling online get the store; everyone else keeps browse-only plans.
  if (activeGym?.features?.store) {
    return <MemberStorePage gymId={activeGymId} gymName={activeGym.displayName || activeGym.name} />;
  }

  return (
    <div className="mpage">
      <div className="row" style={{ gap: 10 }}>
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Back">
          <Icon n="left" s={19} />
        </button>
        <div className="col" style={{ gap: 1 }}>
          <div className="dp dp-l">Plans</div>
          <div className="sub">{activeGym?.displayName || activeGym?.name}</div>
        </div>
      </div>

      {loading ? (
        <>
          {[0, 1, 2].map(i => (
            <div key={i} className="skel" style={{ height: 120, borderRadius: 'var(--r-m)' }} />
          ))}
        </>
      ) : error ? (
        <div className="card flat" style={{ padding: 16 }}>
          <div className="col" style={{ gap: 8 }}>
            <div className="mid">{error}</div>
            <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={load}>Retry</button>
          </div>
        </div>
      ) : plans.length === 0 ? (
        <div className="card flat" style={{ padding: 16 }}>
          <div className="mid">No plans published yet — ask at the front desk.</div>
        </div>
      ) : (
        plans.map(p => {
          const chips = featureChips(p.features as Record<string, any> | null);
          const duration = durationLabel(p.durationMonths);
          return (
            <div key={p.id} className="card" style={{ padding: 16 }}>
              <div className="col" style={{ gap: 10 }}>
                <div className="between" style={{ alignItems: 'flex-start' }}>
                  <div className="col" style={{ gap: 2, minWidth: 0 }}>
                    <div className="dp dp-m trunc">{p.name}</div>
                    {duration && <div className="tiny">{duration}</div>}
                  </div>
                  <div className="col" style={{ gap: 1, alignItems: 'flex-end' }}>
                    <div className="num" style={{ fontSize: 22 }}>{p.price != null ? inr(p.price) : '—'}</div>
                    {p.price != null && duration && <div className="tiny">for {duration}</div>}
                  </div>
                </div>
                {p.description && <div className="sub wrap">{p.description}</div>}
                {chips.length > 0 && (
                  <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                    {chips.map(c => (
                      <span key={c} className="tag">{c}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}

      {!loading && !error && (
        <div className="card tint" style={{ padding: 14 }}>
          <div className="row" style={{ gap: 10 }}>
            <div className="li gold" style={{ width: 32, height: 32, flex: '0 0 32px' }}>
              <Icon n="help" s={17} c="var(--gold-2)" />
            </div>
            <div className="sub wrap">
              Ready to join, or want a custom deal? Talk to the front desk — they can set you up in a minute.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
